/**
 * useMagneticSnap — gravity-well magnetic zones for FAB drag.
 *
 * When the finger enters a zone, the output position is pulled toward
 * the zone center — but still follows the finger with resistance.
 * This creates a "dragging through honey" feel:
 *
 *   outputPos = lerp(fingerPos, zoneCenter, pull)
 *
 * The overlay moves with the finger but is biased toward center.
 * When the finger exits past exitRadius, the overlay snaps back to
 * the raw finger position. A `justExited` flag is returned so the
 * caller can trigger a spring/bounce animation.
 *
 * Outside the zone, the output exactly tracks the raw finger position.
 */

import { useCallback, useRef } from "react"

export interface MagneticZone {
  /** Center X of the zone (px) */
  cx: number
  /** Center Y of the zone (px) */
  cy: number
  /** Radius to enter the zone (px) */
  enterRadius: number
  /** Radius to exit the zone — must be > enterRadius for hysteresis (px) */
  exitRadius: number
  /**
   * Pull strength toward center (0–1).
   * 0.6 = strong pull (overlay mostly at center, moves a little with finger).
   * 0.3 = moderate pull (overlay visibly follows finger with resistance).
   */
  pull: number
}

export interface SnapResult {
  /** The gravity-adjusted position */
  x: number
  y: number
  /** Whether the pointer is inside the zone */
  inside: boolean
  /** True for exactly one frame after the finger exits the zone */
  justExited: boolean
}

/**
 * Gravity-well magnetic snap hook.
 *
 * Returns `snap(rawX, rawY, zone)` — call every touchmove frame.
 * While inside, the output blends between finger and center.
 */
export function useMagneticSnap() {
  const insideRef = useRef(false)
  /** When true, snap is suppressed until finger leaves enterRadius */
  const suppressedRef = useRef(false)

  const reset = useCallback(() => {
    insideRef.current = false
    suppressedRef.current = false
  }, [])

  /**
   * Suppress snap until the finger first moves outside enterRadius.
   * Use when the zone spawns directly under the finger (e.g. cancel zone
   * at FAB origin) to avoid an immediate false snap.
   */
  const suppressUntilExit = useCallback(() => {
    insideRef.current = false
    suppressedRef.current = true
  }, [])

  const snap = useCallback(
    (px: number, py: number, zone: MagneticZone): SnapResult => {
      const dist = Math.hypot(px - zone.cx, py - zone.cy)

      // If suppressed, wait until finger leaves enterRadius before allowing entry
      if (suppressedRef.current) {
        if (dist > zone.enterRadius) {
          suppressedRef.current = false
        } else {
          return { x: px, y: py, inside: false, justExited: false }
        }
      }

      let justExited = false

      // Hysteresis: different thresholds for entering vs leaving
      if (insideRef.current) {
        if (dist > zone.exitRadius) {
          insideRef.current = false
          justExited = true
        }
      } else {
        if (dist < zone.enterRadius) {
          insideRef.current = true
        }
      }

      if (insideRef.current) {
        // Viscous pull: blend finger position toward center.
        // The overlay still follows the finger but is biased toward center.
        // pull=0.6 means output is 60% center + 40% finger.
        const pull = zone.pull
        const sx = px + (zone.cx - px) * pull
        const sy = py + (zone.cy - py) * pull

        return { x: sx, y: sy, inside: true, justExited: false }
      }

      return { x: px, y: py, inside: false, justExited }
    },
    [],
  )

  return { snap, reset, suppressUntilExit }
}
